import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils.js";

/**
 * Tinted card whose accent floods up from the lower edge on hover (mouse or
 * pen), keyboard focus, or `active` -- the same effect as ConfirmationCard,
 * for grid cards such as leave balances or summary tiles. Leaving reverses it.
 *
 * `tone` sets three custom properties the .flood-card rules in tokens.css
 * read: --fc-rest (resting tint), --fc-accent (flood + marks) and --fc-soft
 * (secondary marks). Content opts in to the color flip with the slot classes
 * in `floodCardSlots`; everything else keeps its own classes.
 */
const floodCardVariants = cva(
  "flood-card rounded-[var(--fc-radius)] border border-[var(--color-border)] text-left",
  {
    variants: {
      tone: {
        brand: "[--fc-rest:var(--color-brand-50)] [--fc-accent:var(--color-brand-600)] [--fc-soft:var(--color-brand-200)]",
        info: "[--fc-rest:var(--color-info-bg-subtle)] [--fc-accent:var(--color-info-accent)] [--fc-soft:var(--color-info-border)]",
        success: "[--fc-rest:var(--color-success-bg-subtle)] [--fc-accent:var(--color-success-accent)] [--fc-soft:var(--color-success-border)]",
        warning: "[--fc-rest:var(--color-warning-bg-subtle)] [--fc-accent:var(--color-warning-accent)] [--fc-soft:var(--color-warning-border)]",
        danger: "[--fc-rest:var(--color-error-bg-subtle)] [--fc-accent:var(--color-error-accent)] [--fc-soft:var(--color-error-border)]",
        accent: "[--fc-rest:var(--color-accent-bg-subtle)] [--fc-accent:var(--color-accent-accent)] [--fc-soft:var(--color-accent-border)]",
        rose: "[--fc-rest:var(--color-rose-bg-subtle)] [--fc-accent:var(--color-rose-accent)] [--fc-soft:var(--color-rose-border)]",
        cyan: "[--fc-rest:var(--color-cyan-bg-subtle)] [--fc-accent:var(--color-cyan-accent)] [--fc-soft:var(--color-cyan-border)]",
      },
      /** Resting background: the tone's tint, or the plain card surface. */
      surface: {
        tinted: "bg-[var(--fc-surface)]",
        plain: "bg-surface-0",
      },
      /** How long the flood takes to fill the card; text and marks follow it. */
      speed: {
        fast: "[--fc-duration:400ms]",
        normal: "[--fc-duration:650ms]",
        slow: "[--fc-duration:1000ms]",
      },
      /** The soft accent glow resting at the lower edge before hover. */
      orb: {
        off: "[--fc-orb-opacity:0]",
        low: "[--fc-orb-opacity:0.3]",
        medium: "[--fc-orb-opacity:0.55]",
      },
      /** Lift the card slightly while flooded. */
      lift: {
        true: "[--fc-lift:-2px]",
        false: "[--fc-lift:0px]",
      },
      radius: {
        md: "[--fc-radius:var(--radius-md)]",
        lg: "[--fc-radius:var(--radius-lg)]",
        xl: "[--fc-radius:var(--radius-xl)]",
        "2xl": "[--fc-radius:28px]",
      },
      /** Inner spacing. "none" leaves layout entirely to the content. */
      size: {
        none: "",
        sm: "p-3",
        md: "p-4",
        lg: "p-6",
      },
      /** Set automatically when the card renders as a <button>. */
      interactive: {
        true: "cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
        false: "",
      },
    },
    defaultVariants: {
      tone: "info",
      surface: "tinted",
      speed: "normal",
      orb: "low",
      lift: false,
      radius: "xl",
      size: "md",
      interactive: false,
    },
  }
);

/**
 * Class names for content inside a FloodCard. At rest each keeps whatever
 * color the element already has (except the marks, which take the tone);
 * once flooded they switch to the on-accent white shades.
 * - strong: headline text / big numbers -> white
 * - muted: labels and captions -> white, slightly translucent
 * - mark: bars, dots and swatches in the tone's accent -> white
 * - markSoft: secondary marks (e.g. pending) in the tone's soft shade -> translucent white
 * - track: a progress track -> faint white
 * - icon: an icon box (tinted background + glyph) -> translucent white box, white glyph
 */
export const floodCardSlots = {
  strong: "flood-card__strong",
  muted: "flood-card__muted",
  mark: "flood-card__mark",
  markSoft: "flood-card__mark-soft",
  track: "flood-card__track",
  icon: "flood-card__icon",
} as const;

/** Every default FloodCard falls back to -- pass any of them to change it. */
export const floodCardDefaults = {
  as: "div",
  tone: "info",
  surface: "tinted",
  speed: "normal",
  orb: "low",
  lift: false,
  radius: "xl",
  size: "md",
  animated: true,
} as const;

export type FloodCardTone = NonNullable<VariantProps<typeof floodCardVariants>["tone"]>;

type CardDimension = number | string;

export interface FloodCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "color">,
    Omit<VariantProps<typeof floodCardVariants>, "interactive"> {
  /** Rendered element. "button" makes the whole card the click target (with a focus ring). */
  as?: "div" | "button" | "article" | "section" | "li";
  /** false: no flood at all -- a static tinted card. */
  animated?: boolean;
  /** Force the flooded look without hover, e.g. the selected card or on touch screens. */
  active?: boolean;
  /** Only used when as="button". */
  type?: "button" | "submit" | "reset";
  /** Only used when as="button". */
  disabled?: boolean;
  /** Exact card dimensions: a number is px, a string any CSS length. */
  width?: CardDimension;
  height?: CardDimension;
  minWidth?: CardDimension;
  minHeight?: CardDimension;
  maxWidth?: CardDimension;
  maxHeight?: CardDimension;
}

const FloodCard = React.forwardRef<HTMLElement, FloodCardProps>(
  (
    {
      as = floodCardDefaults.as,
      animated = floodCardDefaults.animated,
      active,
      tone, surface, speed, orb, lift, radius, size,
      width, height, minWidth, minHeight, maxWidth, maxHeight,
      type, className, style, children, onPointerEnter, onPointerLeave,
      ...props
    },
    ref
  ) => {
    const Comp = as as React.ElementType;
    const isButton = as === "button";
    // Caller-chosen dimensions (CLAUDE.md "caller-supplied dimensions on ui/
    // primitives" exception). Undefined keys are dropped.
    const sizeStyle: React.CSSProperties = { width, height, minWidth, minHeight, maxWidth, maxHeight };

    return (
      <Comp
        ref={ref}
        type={isButton ? (type ?? "button") : undefined}
        data-animated={animated ? "" : undefined}
        data-active={active ? "" : undefined}
        // Mouse/pen hover only -- a touch tap would otherwise leave :hover stuck
        // on. Set on the DOM directly so hovering never re-renders.
        onPointerEnter={(e: React.PointerEvent<HTMLElement>) => {
          onPointerEnter?.(e);
          if (e.pointerType !== "touch") e.currentTarget.dataset.hover = "";
        }}
        onPointerLeave={(e: React.PointerEvent<HTMLElement>) => {
          onPointerLeave?.(e);
          delete e.currentTarget.dataset.hover;
        }}
        style={{ ...sizeStyle, ...style }}
        className={cn(floodCardVariants({ tone, surface, speed, orb, lift, radius, size, interactive: isButton }), className)}
        {...props}
      >
        <span aria-hidden="true" className="flood-card__orb" />
        {children}
      </Comp>
    );
  }
);
FloodCard.displayName = "FloodCard";

export { FloodCard, floodCardVariants };
